import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import ShareIcon from '../commons/icons/share_icon';
import SuggestionArticleList from './SuggestedArticleList';
import moment from 'moment';
import { getNewsfeedById } from '../../requests/newsfeed';
import { Article } from './types';
import LoadingContent from 'lqa-ui/lib/Components/LoadingContent';
import { useParams } from 'react-router-dom';
import { setProfilePoint } from '../../requests/profilepic';
import PDFReader from '../../components/commons/PDFReader';
import { AppHeader } from '../../app/common/sharedComponents/AppHeader';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 95px;
`;

const ArticleImage = styled.div`
  height: 382px;
  img {
    object-fit: cover;
    object-position: center;
    width: 100%;
    height: 100%;
  }
`;

const ArticleContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 29px 20px 22px;
  background: white;
  .type {
    color: #4fada6;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    text-align: left;
    text-transform: uppercase;
    margin-bottom: 6px;
  }
  .title {
    font-weight: 600;
    font-size: 28px;
    line-height: 34px;
    color: #031c38;
    margin-bottom: 8px;
  }
  .author {
    font-weight: 400;
    font-size: 15px;
    line-height: 19px;
    color: #2d2d2d;
    margin-bottom: 8px;
    .underline {
      text-decoration: underline;
    }
  }
  .date {
    font-weight: 400;
    font-size: 15px;
    line-height: 19px;
    color: #1f1f1f;
  }
`;

const ArticleContainer = styled.div`
  height: 100vh;
  overflow: scroll;
`;

const ArticleBody = styled.div`
  font-weight: 400;
  font-size: 15px;
  line-height: 21px;
  word-wrap: break-word;
  color: rgba(113, 113, 113, 1);
  margin-top: 13px;
`;

const FirstLine = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: -4px;
`;

const ArticleCTAIcons = styled.div`
  display: flex;
  column-gap: 18px;
`;

const ArticleSuggestionsWrapper = styled.div`
  padding: 23px 20px 0;
  background: #f6f6f6;
  .title {
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
    color: #031c38;
  }
`;

const ArticleReaderView = () => {
  const { t } = useTranslation();
  const bodyContentRef = useRef(null);
  const [articleData, setArticleData] = useState<Article | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);
  const [isSharingSupported] = useState(navigator && 'share' in navigator);
  const { id } = useParams();
  const articleId = Number(id);
  let isCallProfilePointApi: boolean = false;
  const scrollableComponentRef = useRef(null);

  const fetchNewsfeedHandler = async (itemId: number | string) => {
    try {
      return await getNewsfeedById(itemId);
    } catch (err) {}
  };

  const shareIconClickHandler = () => {
    if (isSharingSupported) {
      (navigator as any)
        .share({
          title: 'LQA Client App',
          text: `Check out this Article ${articleData && articleData.title}`,
          url: window.location.href,
        })
        .then(() => {})
        .catch(() => {});
    }
  };

  useEffect(() => {
    (async () => {
      const item = await fetchNewsfeedHandler(articleId);
      setArticleData(item);
      setLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    if (bodyContentRef && bodyContentRef.current && articleData) {
      const contentElement: HTMLElement = bodyContentRef.current as any;
      contentElement.innerHTML = articleData.content;
    }
  }, [articleData]);

  const [startTime, setStartTime] = useState<any>(null);

  // Set the average reading rate range
  const maxReadingRate = 300;

  const wordCount: string | any =
    articleData && articleData.content.split(/\s+/).length;

  const maxTime = (wordCount / maxReadingRate) * 60;

  const expectedTimeSpent = (maxTime / 100) * 80;

  const calculateScrollPercentage = () => {
    const element = scrollableComponentRef.current;
    if (element) {
      // check if actual time spent and expected time spent match when read article 80%
      const endTime: any = new Date();
      const actualTimeSpent = (endTime - startTime) / 1000;
      if (actualTimeSpent >= expectedTimeSpent) {
        // calculate article height and check if scroll is reached 80%
        const { scrollTop, scrollHeight, clientHeight } = element;
        const totalScroll = scrollHeight - clientHeight;
        const calculatedScrollPercentage = (scrollTop / totalScroll) * 100;
        if (calculatedScrollPercentage >= 80 && !isCallProfilePointApi) {
          const data = {
            entityId: articleData && articleData.id,
            entityType: 'NewsFeed',
            activityType: 'NewsPoint',
          };
          isCallProfilePointApi = true;
          setProfilePoint(data)
            .then((res) => {})
            .catch((err) => {
              console.error(err);
              isCallProfilePointApi = false;
            });
        }
      }
    }
  };
  const handleScroll = () => {
    if (!isCallProfilePointApi) {
      calculateScrollPercentage();
    }
  };

  useEffect(() => {
    const element: HTMLElement = scrollableComponentRef.current as any;
    setStartTime(new Date());
    if (element) {
      element.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (element) {
        element.removeEventListener('scroll', handleScroll);
      }
    };
  }, [articleData]);

  return (
    <Wrapper>
      <AppHeader title={t('COMMON.NEWSFEED.TITLES.NEWSFEED')} />
      {loading || !articleData ? (
        <LoadingContent layout="flex" />
      ) : (
        <ArticleContainer ref={scrollableComponentRef}>
          {articleData.media &&
            (articleData.media.endsWith('.pdf') ? (
              <PDFReader pdfUrl={articleData.media} />
            ) : (
              <ArticleImage>
                <img src={articleData.media} />
              </ArticleImage>
            ))}
          <ArticleContent>
            <FirstLine>
              <div className="type">
                {articleData.category ? articleData.category.value : 'All'}
              </div>
              <ArticleCTAIcons>
                {isSharingSupported && (
                  <div onClick={shareIconClickHandler}>
                    <ShareIcon />
                  </div>
                )}
              </ArticleCTAIcons>
            </FirstLine>
            <div className="title">{articleData.title}</div>
            <div className="author">
              By:{' '}
              <span className="underline">
                {articleData.author.userLastName +
                  ' ' +
                  articleData.author.userFirstName}
              </span>
            </div>
            <div className="date">
              {moment(articleData.timestamp).format('MMMM D, YYYY')}
            </div>
            <ArticleBody ref={bodyContentRef} />
          </ArticleContent>
          <ArticleSuggestionsWrapper>
            <div className="title">Other Articles You May Like</div>
            <SuggestionArticleList data={articleData.suggestedArticles || []} />
          </ArticleSuggestionsWrapper>
        </ArticleContainer>
      )}
    </Wrapper>
  );
};

export default ArticleReaderView;
